import { motion } from "motion/react";
import type { ReactNode } from "react";
import { formatShortTime, kindLabel, relationPerspectiveLabel, statusLabel, type Locale } from "../../i18n";
import type { NexusRelation } from "../../domain/mesh";
import type { PresentedEvent } from "../../presentation/mesh";
import { staggerContainer, staggerItem, uiSpring } from "../../lib/motion";

export function InspectorHeader({
  eyebrow,
  title,
  closeLabel,
  onClose,
  mobileExpanded,
  mobileToggleLabel,
  onMobileToggle,
}: {
  eyebrow: string;
  title: string;
  closeLabel: string;
  onClose: () => void;
  mobileExpanded: boolean;
  mobileToggleLabel: string;
  onMobileToggle: () => void;
}) {
  return (
    <motion.div className="inspector__header" variants={staggerContainer} initial="hidden" animate="show">
      <motion.div className="inspector__identity" variants={staggerItem}>
        <span className="micro-label">{eyebrow}</span>
        <h2>{title}</h2>
      </motion.div>
      <motion.div className="inspector__header-actions" variants={staggerItem}>
        <motion.button
          type="button"
          className="icon-button inspector__mobile-toggle"
          aria-label={mobileToggleLabel}
          aria-expanded={mobileExpanded}
          aria-controls="inspector-mobile-details"
          whileHover={{ scale: 1.035 }}
          whileTap={{ scale: 0.94 }}
          transition={uiSpring}
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onMobileToggle();
          }}
        >
          <i aria-hidden="true" />
        </motion.button>
        <motion.button
          className="icon-button"
          variants={staggerItem}
          whileHover={{ scale: 1.035, rotate: 2 }}
          whileTap={{ scale: 0.94 }}
          transition={uiSpring}
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClose();
          }}
          aria-label={closeLabel}
        >
          ×
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

export function StatusLine({
  status,
  kind,
  locale,
}: {
  status: Parameters<typeof statusLabel>[0];
  kind: Parameters<typeof kindLabel>[0];
  locale: Locale;
}) {
  return (
    <motion.div
      className="status-line"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...uiSpring, delay: 0.06 }}
    >
      <span className={`status-orb status-orb--${status}`} aria-hidden="true" />
      <span>{statusLabel(status, locale)}</span>
      <i aria-hidden="true" />
      <span>{kindLabel(kind, locale)}</span>
    </motion.div>
  );
}

export function MetricGrid({ children }: { children: ReactNode }) {
  return (
    <motion.dl className="data-grid" variants={staggerContainer} initial="hidden" animate="show">
      {children}
    </motion.dl>
  );
}
export function Metric({ label, value }: { label: string; value: ReactNode }) {
  return (
    <motion.div variants={staggerItem} layout="position">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </motion.div>
  );
}

export type RelationItem = {
  id: string;
  label: string;
  relation?: NexusRelation;
  outgoing?: boolean;
  kind?: Parameters<typeof kindLabel>[0];
  onSelect: () => void;
};

export function RelationList({ items, locale }: { items: RelationItem[]; locale: Locale }) {
  return (
    <motion.div className="relation-list" variants={staggerContainer} initial="hidden" animate="show">
      {items.map((item) => (
        <motion.button
          key={item.id}
          variants={staggerItem}
          layout="position"
          whileHover={{ x: 3 }}
          whileTap={{ scale: 0.992 }}
          transition={uiSpring}
          onClick={item.onSelect}
        >
          <span>{item.label}</span>
          <small>
            {item.relation
              ? relationPerspectiveLabel(item.relation, item.outgoing ?? true, locale)
              : item.kind
                ? kindLabel(item.kind, locale)
                : "—"}
          </small>
        </motion.button>
      ))}
    </motion.div>
  );
}

export function SignalList({ events, locale }: { events: PresentedEvent[]; locale: Locale }) {
  return (
    <motion.div className="signal-list" variants={staggerContainer} initial="hidden" animate="show">
      {events.slice(0, 3).map((event) => (
        <motion.div className="compact-event" variants={staggerItem} layout="position" key={event.id}>
          <time dateTime={event.timestamp}>{formatShortTime(event.timestamp, locale)}</time>
          <div>
            <strong>{event.title}</strong>
            {event.detail && <span>{event.detail}</span>}
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}
